import React from 'react';
import { ChevronDown, ChevronUp, Check, Video, Clock, Users, Lightbulb } from 'lucide-react';
import { cn } from '../lib/utils';
import { Input } from './ui/input';
import { Label } from './ui/label';

const WALKING_PODCAST_PACKAGES = [
  {
    id: null,
    name: 'Sin Walking Podcast',
    price: 0,
    description: 'No incluir servicio de Walking Podcast',
    features: []
  },
  {
    id: 'wpod_essential',
    name: 'Essential',
    price: 997,
    description: '1hr de Grabación en locación. Post-producción de Audio.',
    features: [
      '1 hora de grabación en locación',
      'Post-producción de Audio',
      'Hasta 2 micrófonos',
      '12 Shorts de 10s',
      'Iluminación móvil'
    ]
  },
  {
    id: 'wpod_platino',
    name: 'Platino',
    price: 3500,
    description: '1hr de Grabación de Video a 1 Cámara en locación. Post-producción de Audio y Video.',
    features: [
      '1 hora de grabación en locación',
      'Video a 1 Cámara',
      'Post-producción de Audio y Video',
      '12 Shorts de 10s',
      'Iluminación móvil'
    ]
  },
  {
    id: 'wpod_premium',
    name: 'Premium',
    price: 5500,
    description: '1hr de Grabación de Video a 2 Cámaras en locación. Post-producción completa.',
    features: [
      '1 hora de grabación en locación',
      'Video a 2 Cámaras',
      'Post-producción de Audio y Video',
      '12 Shorts de 10s',
      'Iluminación móvil'
    ]
  }
];

function CollapsibleSection({ title, icon: Icon, isOpen, onToggle, children, badge }) {
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-[#0f133a]/80 hover:bg-[#1a1f50]/80 transition-colors"
        data-testid="btn-toggle-walking-podcast"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-[#aa2aff] to-[#7012b2]">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-white text-lg">{title}</span>
          {badge && (
            <span className="px-2 py-0.5 bg-[#aa2aff] text-white text-xs rounded-full">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-[#080c2d]/50">
          {children}
        </div>
      )}
    </div>
  );
}

function PackageCard({ pkg, isSelected, onSelect, formatPrice, currency, showPerHour = false }) {
  return (
    <button
      onClick={() => onSelect(pkg.id)}
      data-testid={`btn-wpodcast-${pkg.id}`}
      className={cn(
        "flex flex-col p-4 rounded-xl border text-left transition-all",
        "bg-gradient-to-b from-[#1a1f50]/80 to-[#080c2d]/90",
        isSelected
          ? "border-[#aa2aff] shadow-[0_0_20px_rgba(170,42,255,0.3)]"
          : "border-white/10 hover:border-[#aa2aff]/50"
      )}
    >
      <div className="flex justify-between items-start mb-2">
        <h4 className={cn("font-bold", isSelected ? "text-[#aa2aff]" : "text-white")}>
          {pkg.name}
        </h4>
        <span className="text-lg font-bold text-white">
          {formatPrice(pkg.price)}
          <span className="text-xs text-slate-400">
            {showPerHour && pkg.price > 0 ? '/hr' : ` ${currency}`}
          </span>
        </span>
      </div>
      <p className="text-xs text-slate-400 mb-3">{pkg.description}</p>
      <div className="mt-auto">
        <div className={cn(
          "py-1.5 text-center text-xs font-semibold rounded-lg transition-all",
          isSelected
            ? "bg-[#aa2aff] text-white"
            : "bg-white/5 text-slate-400"
        )}>
          {isSelected ? 'Seleccionado' : 'Seleccionar'}
        </div>
      </div>
    </button>
  );
}

export function WalkingPodcastSection({
  isOpen,
  onToggle,
  selectedPackage,
  onPackageSelect,
  walkingPodcastQuantity = 1,
  onQuantityChange,
  walkingPodcastData = {},
  onDataChange,
  // Walking-specific extras
  sonidoDirectoDays = 0,
  onSonidoDirectoDaysChange,
  asistenteDays = 0,
  onAsistenteDaysChange,
  formatPrice,
  currency,
  servicePrices = {}
}) {
  const packages = WALKING_PODCAST_PACKAGES.map(pkg => ({
    ...pkg,
    price: pkg.id === 'wpod_essential' ? (servicePrices.price_wpod_essential || pkg.price) :
           pkg.id === 'wpod_platino' ? (servicePrices.price_wpod_platino || pkg.price) :
           pkg.id === 'wpod_premium' ? (servicePrices.price_wpod_premium || pkg.price) : pkg.price
  }));

  const selectedPkg = packages.find(p => p.id === selectedPackage);

  // Calculate estimated recording hours
  const estimatedRecordingHours = walkingPodcastData.episodeDuration
    ? (walkingPodcastQuantity * walkingPodcastData.episodeDuration * 2.5) / 60
    : 0;

  // Extra costs
  const priceSonidoDirecto = servicePrices.price_wpod_sonido_directo || 5500;
  const priceAsistente = servicePrices.price_wpod_asistente || 1000;
  const sonidoDirectoTotal = sonidoDirectoDays * priceSonidoDirecto;
  const asistenteTotal = asistenteDays * priceAsistente;
  const extrasTotal = sonidoDirectoTotal + asistenteTotal;

  // Total calculation
  const packageTotal = selectedPkg?.price
    ? (estimatedRecordingHours > 0
        ? (selectedPkg.price * estimatedRecordingHours) + extrasTotal
        : (selectedPkg.price * walkingPodcastQuantity) + extrasTotal)
    : extrasTotal > 0 ? extrasTotal : 0;

  const badge = selectedPackage ? `${formatPrice(packageTotal)}` : null;

  const handleQuantityChange = (value) => {
    const numValue = parseInt(value, 10);
    if (numValue >= 1 && numValue <= 500) {
      onQuantityChange(numValue);
    }
  };

  const handleDataChange = (field, value) => {
    onDataChange({ ...walkingPodcastData, [field]: value });
  };

  return (
    <CollapsibleSection
      title="Walking Podcast"
      icon={Video}
      isOpen={isOpen}
      onToggle={onToggle}
      badge={badge}
    >
      <div className="space-y-4">
        {/* Info banner */}
        <div className="bg-[#aa2aff]/10 rounded-lg p-3 border border-[#aa2aff]/20">
          <p className="text-slate-300 text-xs leading-relaxed">
            Podcast en locación / exteriores. Incluye 1 persona del Crew e iluminación móvil.
            <span className="text-slate-400"> No incluye viáticos.</span>
          </p>
        </div>

        {/* Data Section */}
        <div className="bg-[#1a1f50]/50 rounded-xl p-4 border border-white/10">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Video className="w-4 h-4 text-[#aa2aff]" />
            Datos del Walking Podcast
          </h3>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Título del Podcast</Label>
              <Input
                placeholder="Ej: Mi Walking Podcast"
                value={walkingPodcastData.title || ''}
                onChange={(e) => handleDataChange('title', e.target.value)}
                className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] h-10"
                data-testid="input-wpodcast-title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Cant. de Programas</Label>
                <Input
                  type="number"
                  min="1"
                  max="500"
                  value={walkingPodcastQuantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] h-10"
                  data-testid="wpodcast-quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Duración por Episodio (min)</Label>
                <Input
                  type="number"
                  min="1"
                  max="180"
                  placeholder="Ej: 30"
                  value={walkingPodcastData.episodeDuration || ''}
                  onChange={(e) => handleDataChange('episodeDuration', parseInt(e.target.value) || 0)}
                  className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] h-10"
                  data-testid="input-wpodcast-duration"
                />
              </div>
            </div>

            {estimatedRecordingHours > 0 && (
              <div className="bg-[#aa2aff]/10 rounded-lg p-3 border border-[#aa2aff]/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#aa2aff]" />
                    <span className="text-slate-300 text-sm">Horas de Grabación Estimadas</span>
                  </div>
                  <span className="text-white font-bold text-lg">
                    {estimatedRecordingHours.toFixed(1)} hrs
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Package Selection */}
        <div>
          <h3 className="text-white font-semibold mb-3">Selecciona tu Paquete</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {packages.map(pkg => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                isSelected={selectedPackage === pkg.id}
                onSelect={onPackageSelect}
                formatPrice={formatPrice}
                currency={currency}
                showPerHour={estimatedRecordingHours > 0}
              />
            ))}
          </div>
        </div>

        {/* Walking-specific extras */}
        {selectedPackage && (
          <div className="pt-4 border-t border-white/10">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-[#aa2aff]" />
              Equipo Adicional
            </h3>
            <div className="space-y-3">
              {/* Sonido Directo */}
              <div className={cn(
                "flex items-center justify-between p-3 rounded-lg border transition-all",
                sonidoDirectoDays > 0
                  ? "border-[#aa2aff] bg-[#aa2aff]/10"
                  : "border-white/10 bg-white/5"
              )}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-[#aa2aff]" />
                    <span className="text-white text-sm font-medium">Unidad de Sonido Directo</span>
                  </div>
                  <p className="text-slate-400 text-xs mt-0.5 ml-6">{formatPrice(priceSonidoDirecto)} {currency} / día</p>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-slate-400 text-xs">Días:</Label>
                  <Input
                    type="number"
                    min="0"
                    max="30"
                    value={sonidoDirectoDays || ''}
                    onChange={(e) => onSonidoDirectoDaysChange(parseInt(e.target.value) || 0)}
                    className="bg-[#080c2d]/50 border-[#272b96] text-white w-20 h-8 text-sm focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff]"
                    data-testid="input-sonido-directo-days"
                  />
                </div>
              </div>

              {/* Asistente */}
              <div className={cn(
                "flex items-center justify-between p-3 rounded-lg border transition-all",
                asistenteDays > 0
                  ? "border-[#aa2aff] bg-[#aa2aff]/10"
                  : "border-white/10 bg-white/5"
              )}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#aa2aff]" />
                    <span className="text-white text-sm font-medium">Asistente</span>
                  </div>
                  <p className="text-slate-400 text-xs mt-0.5 ml-6">{formatPrice(priceAsistente)} {currency} / día</p>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-slate-400 text-xs">Días:</Label>
                  <Input
                    type="number"
                    min="0"
                    max="30"
                    value={asistenteDays || ''}
                    onChange={(e) => onAsistenteDaysChange(parseInt(e.target.value) || 0)}
                    className="bg-[#080c2d]/50 border-[#272b96] text-white w-20 h-8 text-sm focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff]"
                    data-testid="input-asistente-days"
                  />
                </div>
              </div>

              {/* Extras subtotal */}
              {extrasTotal > 0 && (
                <div className="text-right">
                  <span className="text-slate-400 text-xs">Equipo adicional: </span>
                  <span className="text-[#aa2aff] font-bold text-sm">{formatPrice(extrasTotal)} {currency}</span>
                </div>
              )}

              {/* Note */}
              <p className="text-slate-500 text-xs italic">
                * No incluye viáticos. Incluye 1 persona del Crew e iluminación móvil.
              </p>
            </div>
          </div>
        )}

        {/* Total Summary */}
        {selectedPackage && packageTotal > 0 && (
          <div className="pt-4 border-t border-white/10">
            <div className="flex justify-between items-center text-lg">
              <span className="text-white font-semibold">Subtotal Walking Podcast:</span>
              <span className="text-[#aa2aff] font-bold">{formatPrice(packageTotal)} {currency}</span>
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

export default WalkingPodcastSection;
